import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import AuthContext from './AuthContext';

const PaymentContext = createContext();

export const usePayment = () => {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
};

export const PaymentProvider = ({ children }) => {
  const { user, typeOfUser } = useContext(AuthContext);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAction, setPaymentAction] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  const functions = getFunctions();

  // Check if user has active subscription
  const checkSubscription = async () => {
    if (!user?.uid || typeOfUser !== 'SCHOOLADMIN') return;

    setIsLoading(true);
    try {
      const checkSubscriptionFunction = httpsCallable(functions, 'checkSubscription');
      const result = await checkSubscriptionFunction({ userId: user.uid });
      
      if (result.data.status) {
        setSubscription(result.data.subscription);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel subscription
  const cancelSubscription = async () => {
    if (!user?.uid || !subscription) return { success: false, error: 'No active subscription' };

    setIsLoading(true);
    try {
      const cancelSubscriptionFunction = httpsCallable(functions, 'cancelSubscription');
      const result = await cancelSubscriptionFunction({
        userId: user.uid,
        subscriptionId: subscription.stripeSubscriptionId
      });

      if (result.data.status) {
        setSubscription(null);
        return { success: true };
      } else {
        return { success: false, error: result.data.message };
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      return { success: false, error: 'Failed to cancel subscription' };
    } finally {
      setIsLoading(false);
    }
  };

  // Check if payment is required for action
  const requirePayment = (action) => {
    if (typeOfUser !== 'SCHOOLADMIN') return false;
    
    // If no subscription, payment is required
    if (!subscription) {
      setPaymentAction(action);
      setShowPaymentModal(true);
      return true;
    }

    // Check if subscription is active
    if (subscription.status !== 'active') {
      setPaymentAction(action);
      setShowPaymentModal(true);
      return true;
    }

    return false;
  };

  // Get subscription plans
  const getSubscriptionPlans = async () => {
    try {
      const getPlansFunction = httpsCallable(functions, 'getSubscriptionPlans');
      const result = await getPlansFunction();
      
      if (result.data.status) {
        return result.data.plans;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
      return [];
    }
  };

  // Get payment history
  const getPaymentHistory = async () => {
    if (!user?.uid) return [];

    try {
      const getPaymentHistoryFunction = httpsCallable(functions, 'getPaymentHistory');
      const result = await getPaymentHistoryFunction({ userId: user.uid });
      
      if (result.data.status) {
        return result.data.payments;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      return [];
    }
  };

  // Clear payment error
  const clearPaymentError = () => {
    setPaymentError(null);
  };

  // Close payment modal
  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentAction(null);
    setPaymentError(null);
  };

  useEffect(() => {
    if (user?.uid && typeOfUser === 'SCHOOLADMIN') {
      checkSubscription();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, typeOfUser]);

  const value = {
    subscription,
    isLoading,
    showPaymentModal,
    paymentAction,
    paymentError,
    checkSubscription,
    cancelSubscription,
    requirePayment,
    getSubscriptionPlans,
    getPaymentHistory,
    clearPaymentError,
    closePaymentModal,
    setShowPaymentModal
  };

  return (
    <PaymentContext.Provider value={value}>
      {children}
    </PaymentContext.Provider>
  );
}; 